import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';
import Switch from '@mui/material/Switch';
import type { RemirrorJSON } from '@remirror/core';
import { ChatContextType } from '@trinity/chat';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { InteractionModeDropdown } from '@trinity/chat-components/components/DigitalTwin/InteractionModeDropdown';
import { SoundInput } from '@trinity/chat-components/components/icons/SoundInput';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import {
  useChatInteractionModes,
  useHasChatAtMentionFilterEnabled,
  useHasChatVoiceInputEnabled,
  useHasChatVoiceModeEnabled,
} from '@trinity/chat-components/containers/Chat/selectors';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { pulse } from '@trinity/chat-components/utils';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import {
  SubmitMessageContext,
  UploadFilesContext,
  IsPreparingUploadContext,
  ChatHasAccessContext,
  ChatInputFeaturesContext,
  ActiveChatSessionContext,
  EditorDocContext,
} from './context';
import { useCancelQueryMutation } from './hooks/useCancelQueryMutation';
import type { MicState } from './hooks/useSpeechRecognition';
import { UploadFilesButton } from './Input.Upload';
import type { AtMentionContextValue } from './RemirrorInput/AtMentionContext';
import { RemirrorChatInput } from './RemirrorInput/RemirrorChatInput';
import { SendIcon } from './SendIcon';

export const UnsubmittedMessage = () => {
  const { remirrorRef, soundLevelRef, speechRecognition, onClickMicrophone } =
    useContext(InputControlsContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { state: submitState, onSubmit: handleSubmit } = useContext(SubmitMessageContext);
  const hasChatVoiceInputEnabled = useHasChatVoiceInputEnabled();
  const features = useGroupFeatures();
  const inputFeatures = useContext(ChatInputFeaturesContext);
  const showUploadFile = features.chatFileUploads && inputFeatures.fileUpload !== false;
  const chatAtMentionFilter = useHasChatAtMentionFilterEnabled();

  const uploadFiles = useContext(UploadFilesContext);
  const isPreparingUpload = useContext(IsPreparingUploadContext);
  const chat = useContext(ActiveChatSessionContext);
  const editorDocRef = useContext(EditorDocContext);
  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifierNullSafe();

  const hasAccess = useContext(ChatHasAccessContext);

  const inputDisabled = useMemo(() => {
    return !hasAccess || submitState.status === 'submitting-query' || submitState.status === 'uploading-file';
  }, [submitState.status, hasAccess]);

  const micDisabled = useMemo(() => {
    return !hasAccess || submitState.status === 'submitting-query' || submitState.status === 'uploading-file';
  }, [hasAccess, submitState.status]);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.key === 'Enter' && e.shiftKey) {
      // Remirror handles Shift+Enter natively (inserts newline)
      return;
    }
  }, []);

  const activeContextIdentifier = useMemo(() => {
    if (chat.context.type === ChatContextType.Agent) {
      return chat.context.data.identifier;
    }
    return null;
  }, [chat.context]);

  const activeContextData = useMemo(() => {
    if (chat.context.type !== ChatContextType.Agent) return null;
    return chat.context.data;
  }, [chat.context]);

  const mentionsEnabled = useMemo(() => {
    return chatAtMentionFilter && (activeContextData?.subjectAutocomplete?.chatAutocompleteEnabled ?? false);
  }, [chatAtMentionFilter, activeContextData]);

  const atMentionContextValue = useMemo((): AtMentionContextValue | undefined => {
    if (!mentionsEnabled || !activeContextIdentifier) return undefined;
    return [
      activeContextIdentifier,
      async (query: string, pagination) => {
        const response = await apiClient.chatContext.getContextSubjectSearch({
          contextIdentifier: activeContextIdentifier,
          query,
          brandIdentifier,
          limit: pagination?.limit,
          offset: pagination?.offset,
        });

        return {
          results: response.results
            .map((item) => ({
              entityId: item.entityId ?? item.name,
              objectId: item.objectId,
              name: item.name,
              type: item.type,
              link: item.link,
              subtitle: item.subtitle,
            }))
            .filter((item) => item.entityId !== undefined),
        };
      },
      activeContextData?.subjectAutocomplete?.displayConfig ?? undefined,
    ];
  }, [mentionsEnabled, activeContextIdentifier, apiClient.chatContext, activeContextData, brandIdentifier]);

  const handleTextChange = useCallback(
    (text: string) => {
      dispatch({
        type: 'input-changed',
        payload: { value: text },
      });
    },
    [dispatch]
  );

  const handleDocChange = useCallback(
    (doc: RemirrorJSON) => {
      editorDocRef.current = doc;
    },
    [editorDocRef]
  );

  const handleSubmitAndClear = useCallback(() => {
    handleSubmit();
    remirrorRef.current?.clearAndSetText('');
  }, [handleSubmit, remirrorRef]);

  const micState = speechRecognition.micState;

  const showVoiceFeatures = useMemo(
    () => speechRecognition.isSupported() && hasChatVoiceInputEnabled && inputFeatures.voiceInput !== false,
    [speechRecognition, hasChatVoiceInputEnabled, inputFeatures.voiceInput]
  );

  const { isDigitalTwin } = useAvatarPanel();

  const placeholder = speechRecognition.listening ? 'Speak your question.' : 'Type your question.';

  return (
    <Root>
      <InputWrap onKeyDown={handleKeyDown}>
        <RemirrorChatInput
          key={mentionsEnabled ? 'mentions-enabled' : 'no-mentions'}
          ref={remirrorRef}
          placeholder={placeholder}
          disabled={inputDisabled}
          onSubmit={handleSubmitAndClear}
          onTextChange={handleTextChange}
          onDocChange={handleDocChange}
          mentionsEnabled={mentionsEnabled}
          atMentionContextValue={atMentionContextValue}
        />

        <Buttons>
          <InlineButtons>
            {showVoiceFeatures && (
              <Tooltip title={micState === 'error' ? speechRecognition.errorMsg : null} placement="top">
                <StyledMicButton
                  disabled={micDisabled || micState === 'error'}
                  micState={micState}
                  onClick={onClickMicrophone}
                >
                  <MicIconButton micState={micState} soundLevelRef={soundLevelRef} />
                </StyledMicButton>
              </Tooltip>
            )}

            <SubmitButton />
          </InlineButtons>
          <InteractionButtons />
        </Buttons>
        {/* File upload is not supported in Digital Twin v1; kept in DOM with visibility:hidden to preserve layout */}
        {showUploadFile && (
          <StyledUploadButton
            isInvisible={isDigitalTwin}
            isLoading={isPreparingUpload}
            onSelect={uploadFiles}
          />
        )}
      </InputWrap>
    </Root>
  );
};

const InteractionButtons = () => {
  const { isDigitalTwin, isNewChatInteractionLayout } = useAvatarPanel();
  const hasVoiceModeFeature = useHasChatVoiceModeEnabled();
  const voicemode = useContext(VoiceModeContext);
  const { onClickVoiceMode } = useContext(InputControlsContext);
  const settingsInteractionModes = useChatInteractionModes();
  const inputFeatures = useContext(ChatInputFeaturesContext);

  if (inputFeatures.voiceInput === false) return null;

  if (isDigitalTwin || (isNewChatInteractionLayout && settingsInteractionModes.available.length > 1)) {
    return (
      <VoiceModeButton>
        <InteractionModeDropdown />
      </VoiceModeButton>
    );
  }

  if (hasVoiceModeFeature) {
    return (
      <VoiceModeButton>
        <VoiceModeInput>
          <div>Voice</div>
          <Switch value={voicemode?.enabled ?? false} onChange={onClickVoiceMode} />
        </VoiceModeInput>
      </VoiceModeButton>
    );
  }
};

interface MicIconButtonProps {
  micState: MicState;
  soundLevelRef: React.RefObject<HTMLDivElement>;
}

const MicIconButton = ({ micState, soundLevelRef }: MicIconButtonProps) => {
  if (micState === 'listening' || micState === 'waiting-for-permission') {
    return <SoundInput ref={soundLevelRef} color="white" paused={micState === 'waiting-for-permission'} />;
  } else if (micState === 'error') {
    return <StyledMicOffIcon />;
  } else {
    return <StyledMicIcon />;
  }
};

const SubmitButton = () => {
  const { onClickSubmit } = useContext(InputControlsContext);
  const { state } = useContext(SubmitMessageContext);
  const cancelQuery = useCancelQueryMutation();

  const disabled = useMemo(() => {
    return ['disabled', 'uploading-file'].includes(state.status);
  }, [state.status]);

  const handleClick = useCallback(async () => {
    if (disabled) return;
    if (state.status === 'inactive') {
      return onClickSubmit();
    } else if (state.status === 'submitting-query') {
      await cancelQuery.mutateAsync();
    }
  }, [disabled, state.status, cancelQuery, onClickSubmit]);

  const isLoading = state.status === 'submitting-query';

  return (
    <Button disabled={disabled} isLoading={isLoading} onClick={handleClick}>
      {isLoading ? <StyledStopIcon /> : <StyledSendIcon disabled={disabled} size={40} />}
    </Button>
  );
};

const baseTextareaPadding = 10;
const submitBtnSize = 38;
const buttonsSpacing = 10;

const Root = styled('div')({
  // width: messageLaneWidth,
  width: '100%',
  margin: '0 auto',
  position: 'relative',
  // paddingTop: '70px',
});

export const InputWrap = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginBottom: 10,
});

const Buttons = styled.div({
  // display: 'flex',
  // gap: buttonsSpacing,
  position: 'absolute',
  right: 10,
});

const InlineButtons = styled.div({
  display: 'flex',
  gap: buttonsSpacing,
});

const Button = styled('button', { shouldForwardProp: shouldForwardProp('isLoading') })<{
  isLoading?: boolean;
}>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  width: ${submitBtnSize}px;
  height: ${submitBtnSize}px;
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;

  transition:
    background-color 0.3s,
    border 0.3s;

  background-color: ${(props) => {
    if (props.disabled) return props.theme.palette.gray.light1;
    if (props.isLoading) return '#f2f2f2';
    return props.theme.palette.sentiment.primary.main;
  }};
  border: ${(props) => (props.isLoading ? '2px solid #d4d8de' : '2px solid transparent')};

  &:hover:not([disabled]) {
    background-color: ${(props) =>
      props.isLoading ? '#e6e6e6' : props.theme.palette.sentiment.primary.dark};
  }
`;

const VoiceModeButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 100%;
  height: 40px;
`;

const VoiceModeInput = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-family: var(--font-bold);
`;

const StyledStopIcon = styled(StopIcon)`
  color: #4c4c4c;
`;

type SendProps = {
  disabled: boolean;
};

const StyledSendIcon = styled(SendIcon, { shouldForwardProp: shouldForwardProp('disabled') })<SendProps>`
  padding-left: 4px;
  fill: ${(props) => (props.disabled ? props.theme.palette.gray.dark1 : props.theme.palette.white.main)};

  transition: fill 0.3s;
`;

const StyledMicIcon = styled(MicIcon, {})`
  color: var(--black-l);
`;

const StyledMicOffIcon = styled(MicOffIcon)`
  color: var(--black-l);
`;

type StyledMicButtonProps = {
  micState: MicState;
};

const StyledMicButton = styled('button')<StyledMicButtonProps>(({ theme, micState, disabled }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',

  width: `${submitBtnSize}px`,
  height: `${submitBtnSize}px`,
  borderRadius: '50%',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  transition: 'background-color 0.3s',

  boxShadow: `0 0 0 0 ${theme.palette.sentiment.primary.main}`,
  animation: micState === 'listening' ? `${pulse(theme.palette.sentiment.primary.main)} 4s infinite` : 'none',
  backgroundColor:
    micState === 'listening' || micState === 'waiting-for-permission'
      ? theme.palette.sentiment.primary.main
      : 'none',

  '&:hover:not([disabled])': {
    backgroundColor: (() => {
      if (micState === 'listening' || micState === 'waiting-for-permission') return undefined;
      if (disabled) return theme.palette.gray.light1;
      return theme.palette.sentiment.primary.light;
    })(),
  },

  '&:hover:not([disabled]) svg': {
    color: theme.palette.sentiment.primary.dark,
    //backgroundColor: listening ? undefined : disabled ? theme.palette.gray.light1 : theme.palette.sentiment.primary.light,
  },
}));

const StyledUploadButton = styled(UploadFilesButton, {
  shouldForwardProp: shouldForwardProp('isInvisible'),
})<{
  isInvisible?: boolean;
}>(({ isInvisible }) => ({
  position: 'absolute',
  bottom: baseTextareaPadding,
  left: baseTextareaPadding,
  // File upload not supported in Digital Twin v1 — hidden but kept in DOM to preserve layout geometry
  ...(isInvisible && {
    visibility: 'hidden',
    pointerEvents: 'none',
  }),
}));
